import { DimensionCubeFields, emptyDimensions, type DimensionValue } from "./DimensionCubeFields";

export const HDX_TOTE_TYPE = "HDX Tough Tote";
export const CUSTOM_OPTION = "__custom__";
export const BIN_COLORS = ["Black", "Transparent"] as const;
export const LID_COLORS = ["Yellow", "Olive", "Red"] as const;

export const HDX_TOTE_SIZES = [
  "6.5 qt", "7 gal", "9 gal", "12 gal", "14 gal", "17 gal",
  "27 gal", "38 gal", "40 gal", "55 gal", "57 gal"
] as const;

export interface ToteDetailsValue extends DimensionValue {
  typeChoice: string;
  customType: string;
  sizeChoice: string;
  customSize: string;
  binColor: string;
  lidColor: string;
}

export function newToteDetails(): ToteDetailsValue {
  return {
    typeChoice: HDX_TOTE_TYPE,
    customType: "",
    sizeChoice: "",
    customSize: "",
    binColor: "Black",
    lidColor: "",
    ...emptyDimensions()
  };
}

export function toteDetailsFromRecord(
  containerType: string | null,
  containerSize: string | null,
  containerColor: string | null,
  lidColor: string | null,
  dimensions: DimensionValue = emptyDimensions()
): ToteDetailsValue {
  const knownSize = HDX_TOTE_SIZES.includes((containerSize || "") as (typeof HDX_TOTE_SIZES)[number]);
  return {
    typeChoice: !containerType || containerType === HDX_TOTE_TYPE ? HDX_TOTE_TYPE : CUSTOM_OPTION,
    customType: containerType && containerType !== HDX_TOTE_TYPE ? containerType : "",
    sizeChoice: !containerSize || knownSize ? containerSize || "" : CUSTOM_OPTION,
    customSize: containerSize && !knownSize ? containerSize : "",
    binColor: BIN_COLORS.includes(containerColor as (typeof BIN_COLORS)[number]) ? containerColor! : "Black",
    lidColor: LID_COLORS.includes(lidColor as (typeof LID_COLORS)[number]) ? lidColor! : "",
    ...dimensions
  };
}

export function toteDetailsPayload(value: ToteDetailsValue) {
  const custom = value.typeChoice === CUSTOM_OPTION;
  return {
    container_type: custom ? value.customType.trim() || "Other tote" : HDX_TOTE_TYPE,
    container_size: value.sizeChoice === CUSTOM_OPTION ? value.customSize.trim() || null : value.sizeChoice || null,
    container_color: value.binColor,
    lid_color: value.lidColor || null,
    length: custom && value.length ? value.length : null,
    width: custom && value.width ? value.width : null,
    height: custom && value.height ? value.height : null
  };
}

export function ToteDetailsFields({ value, onChange }: {
  value: ToteDetailsValue;
  onChange: (next: ToteDetailsValue) => void;
}) {
  return (
    <fieldset className="tote-details-fields">
      <legend>Tote details</legend>
      <p className="field-help">Choose the matching HDX Tough Tote capacity, or enter a custom tote.</p>
      <div className="form-grid two">
        <label>
          Tote make/model
          <select value={value.typeChoice} onChange={(event) => onChange({ ...value, typeChoice: event.target.value })}>
            <option value={HDX_TOTE_TYPE}>HDX Tough Tote</option>
            <option value={CUSTOM_OPTION}>Other / custom tote</option>
          </select>
        </label>
        {value.typeChoice === CUSTOM_OPTION && (
          <label>Custom tote type<input value={value.customType} onChange={(event) => onChange({ ...value, customType: event.target.value })} placeholder="Rubbermaid ActionPacker" /></label>
        )}
        <label>
          Capacity
          <select value={value.sizeChoice} onChange={(event) => onChange({ ...value, sizeChoice: event.target.value })}>
            <option value="">Select a size</option>
            {HDX_TOTE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
            <option value={CUSTOM_OPTION}>Other / custom size</option>
          </select>
        </label>
        {value.sizeChoice === CUSTOM_OPTION && (
          <label>Custom capacity<input value={value.customSize} onChange={(event) => onChange({ ...value, customSize: event.target.value })} placeholder="Example: 20 gal" /></label>
        )}
        <label>
          Bin color
          <select value={value.binColor} onChange={(event) => onChange({ ...value, binColor: event.target.value })}>
            {BIN_COLORS.map((color) => <option key={color}>{color}</option>)}
          </select>
        </label>
        <label>
          Lid color
          <select value={value.lidColor} onChange={(event) => onChange({ ...value, lidColor: event.target.value })}>
            <option value="">Select lid color</option>
            {LID_COLORS.map((color) => <option key={color}>{color}</option>)}
          </select>
        </label>
      </div>
      {value.typeChoice === CUSTOM_OPTION && (
        <DimensionCubeFields value={value} onChange={(dimensions) => onChange({ ...value, ...dimensions })} />
      )}
    </fieldset>
  );
}
